// src/pages/admin/Banners.jsx
import React, { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Switch,
  Grid,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  getBanners,
  createBanner,
  updateBanner,
  deleteBanner,
} from "../../services/api";

export default function Banners() {
  const [banners, setBanners] = useState([]);
  const [form, setForm] = useState({
    smallText: "",
    heading1: "",
    highlightText: "",
    subHeading: "",
    ctaText: "",
    ctaLink: "",
    statsTitle: "",
    statsValue: "",
    statsGrowth: "",
  });
  const [images, setImages] = useState({
    bgImage: null,
    mainImage: null,
    patternImage: null,
    logoImage: null,
  });
  const [previews, setPreviews] = useState({
    bgImage: null,
    mainImage: null,
    patternImage: null,
    logoImage: null,
  });
  const [editingId, setEditingId] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const loadBanners = async () => {
    try {
      const res = await getBanners();
      setBanners(res.data.data || []);
    } catch (err) {
      console.error("load banners", err);
    }
  };

  useEffect(() => {
    loadBanners();
  }, []);

  const handleFile = (field, file) => {
    setImages((s) => ({ ...s, [field]: file || null }));
    setPreviews((s) => ({ ...s, [field]: file ? URL.createObjectURL(file) : null }));
  };

  const resetForm = () => {
    setForm({
      smallText: "",
      heading1: "",
      highlightText: "",
      subHeading: "",
      ctaText: "",
      ctaLink: "",
      statsTitle: "",
      statsValue: "",
      statsGrowth: "",
    });
    setImages({ bgImage: null, mainImage: null, patternImage: null, logoImage: null });
    setPreviews({ bgImage: null, mainImage: null, patternImage: null, logoImage: null });
    setEditingId(null);
  };

  const handleSubmit = async () => {
    try {
      const fd = new FormData();
      // string fields
      Object.entries(form).forEach(([k, v]) => { if (v !== undefined && v !== null) fd.append(k, v); });
      // image files (only append if selected)
      if (images.bgImage) fd.append("bgImage", images.bgImage);
      if (images.mainImage) fd.append("mainImage", images.mainImage);
      if (images.patternImage) fd.append("patternImage", images.patternImage);
      if (images.logoImage) fd.append("logoImage", images.logoImage);

      if (editingId) {
        await updateBanner(editingId, fd);
        setSnack({ open: true, message: "Banner updated", severity: "success" });
      } else {
        await createBanner(fd);
        setSnack({ open: true, message: "Banner created", severity: "success" });
      }

      resetForm();
      await loadBanners();
    } catch (err) {
      console.error("save banner", err);
      setSnack({ open: true, message: (err?.response?.data?.error || "Error saving banner"), severity: "error" });
    }
  };

  const handleEdit = (b) => {
    setEditingId(b.id);
    setForm({
      smallText: b.smallText || "",
      heading1: b.heading1 || "",
      highlightText: b.highlightText || "",
      subHeading: b.subHeading || "",
      ctaText: b.ctaText || "",
      ctaLink: b.ctaLink || "",
      statsTitle: b.statsTitle || "",
      statsValue: b.statsValue || "",
      statsGrowth: b.statsGrowth || "",
    });
    // show preview images if they exist (full URL)
    const base = process.env.REACT_APP_API_URL || window.location.origin;
    const wrap = (url) => (url ? (url.startsWith("http") ? url : `${base}${url}`) : null);
    setPreviews({
      bgImage: wrap(b.bgImage),
      mainImage: wrap(b.mainImage),
      patternImage: wrap(b.patternImage),
      logoImage: wrap(b.logoImage),
    });
    setImages({ bgImage: null, mainImage: null, patternImage: null, logoImage: null });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete banner?")) return;
    try {
      await deleteBanner(id);
      setSnack({ open: true, message: "Banner deleted", severity: "success" });
      await loadBanners();
    } catch (err) {
      setSnack({ open: true, message: "Failed to delete", severity: "error" });
    }
  };

  return (
    <Container maxWidth="md" sx={{ pt: 5 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" mb={2}>{ editingId ? "Edit Banner" : "Add Banner" }</Typography>

        <Box display="grid" gap={2} mb={2}>
          <TextField label="Small Text" value={form.smallText} onChange={(e)=>setForm({...form, smallText: e.target.value})} />
          <TextField label="Heading" value={form.heading1} onChange={(e)=>setForm({...form, heading1: e.target.value})} />
          <TextField label="Highlight Text" value={form.highlightText} onChange={(e)=>setForm({...form, highlightText: e.target.value})} />
          <TextField label="Subheading" value={form.subHeading} onChange={(e)=>setForm({...form, subHeading: e.target.value})} />
          <TextField label="CTA Text" value={form.ctaText} onChange={(e)=>setForm({...form, ctaText: e.target.value})} />
          <TextField label="CTA Link" value={form.ctaLink} onChange={(e)=>setForm({...form, ctaLink: e.target.value})} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">Background Image</Typography>
              <input type="file" accept="image/*" onChange={(e)=>handleFile('bgImage', e.target.files?.[0] || null)} />
              {previews.bgImage && <Box mt={1}><img src={previews.bgImage} alt="bg" style={{width: '100%', maxHeight: 160, objectFit:'cover'}}/></Box>}
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">Main Image</Typography>
              <input type="file" accept="image/*" onChange={(e)=>handleFile('mainImage', e.target.files?.[0] || null)} />
              {previews.mainImage && <Box mt={1}><img src={previews.mainImage} alt="main" style={{width: '100%', maxHeight: 160, objectFit:'cover'}}/></Box>}
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">Pattern Image</Typography>
              <input type="file" accept="image/*" onChange={(e)=>handleFile('patternImage', e.target.files?.[0] || null)} />
              {previews.patternImage && <Box mt={1}><img src={previews.patternImage} alt="pattern" style={{width: '100%', maxHeight: 120, objectFit:'cover'}}/></Box>}
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">Logo Image</Typography>
              <input type="file" accept="image/*" onChange={(e)=>handleFile('logoImage', e.target.files?.[0] || null)} />
              {previews.logoImage && <Box mt={1}><img src={previews.logoImage} alt="logo" style={{width: '100%', maxHeight: 80, objectFit:'contain'}}/></Box>}
            </Grid>
          </Grid>

          <TextField label="Stats Title" value={form.statsTitle} onChange={(e)=>setForm({...form, statsTitle: e.target.value})} />
          <TextField label="Stats Value" value={form.statsValue} onChange={(e)=>setForm({...form, statsValue: e.target.value})} />
          <TextField label="Stats Growth" value={form.statsGrowth} onChange={(e)=>setForm({...form, statsGrowth: e.target.value})} />

          <Box display="flex" gap={2}>
            <Button variant="contained" onClick={handleSubmit}>{editingId ? "Update Banner" : "Create Banner"}</Button>
            <Button variant="outlined" onClick={resetForm}>Reset</Button>
          </Box>
        </Box>

        <Typography variant="h6" mt={3}>Existing Banners</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Heading</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {banners.map(b => (
              <TableRow key={b.id}>
                <TableCell>{b.id}</TableCell>
                <TableCell>{b.heading1}</TableCell>
                <TableCell><Switch checked={b.isActive} disabled /></TableCell>
                <TableCell>
                  <Tooltip title="Edit"><IconButton onClick={() => handleEdit(b)}><EditIcon/></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton color="error" onClick={() => handleDelete(b.id)}><DeleteIcon/></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({...snack, open:false})}>
          <Alert severity={snack.severity} onClose={() => setSnack({...snack, open:false})}>{snack.message}</Alert>
        </Snackbar>
      </Paper>
    </Container>
  );
}
